/**
 * Creative Memory Overview — section landing page at /creative-memory.
 *
 * Stats roll-up across all playbooks for the selected client's org, plus
 * deep-links into the section's sub-views. v1 derives counts from the
 * existing /playbooks and /escalations endpoints; the Phase 5 PRD covers
 * the proper rollup endpoint (and the indexed counts that go with it).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Gauge,
  BookOpen,
  Route as RouteIcon,
  History,
  AlertTriangle,
  Cpu,
} from 'lucide-react';
import { useClientStore } from '@/stores/client';
import {
  creativeMemoryApi,
  type CreativeMemoryActivityItem,
  type PlaybookListItem,
} from '@/services/api';

interface OverviewStats {
  playbooks: PlaybookListItem[];
  recentDecisions: CreativeMemoryActivityItem[];
  routingDecisionsLast7d: number;
  escalationsOpen: number;
  loading: boolean;
  error: string | null;
}

const since7d = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
};

export function CreativeMemoryOverviewPage() {
  const selectedClient = useClientStore((s) => s.selectedClient());
  const clientUid = selectedClient?.client_uid;
  const orgUid = selectedClient?.org_uid;

  const [stats, setStats] = useState<OverviewStats>({
    playbooks: [],
    recentDecisions: [],
    routingDecisionsLast7d: 0,
    escalationsOpen: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setStats((s) => ({ ...s, loading: true, error: null }));
    Promise.all([
      creativeMemoryApi.listPlaybooks().catch((err) => ({ error: err })),
      creativeMemoryApi
        .listDecisions({
          decision_type: 'persona_routing',
          client_uid: clientUid,
          since: since7d(),
          limit: 100,
        })
        .catch((err) => ({ error: err })),
      // Escalations API does not yet take a client filter — show org-wide
      // count as a coarse proxy until the index lands (PRD §4).
      fetch(`/api/v1/creative-memory/escalations?limit=200`)
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .catch(() => ({ items: [] })),
    ]).then(([playbooks, decisions, escalations]: any[]) => {
      if (cancelled) return;
      const firstError =
        playbooks?.error?.message ||
        decisions?.error?.message ||
        null;
      setStats({
        playbooks: playbooks?.items || [],
        recentDecisions: decisions?.items || [],
        routingDecisionsLast7d: decisions?.total || 0,
        escalationsOpen: escalations?.items?.length || 0,
        loading: false,
        error: firstError,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  const totalRules = useMemo(
    () => stats.playbooks.reduce((acc, p) => acc + (p.rule_count || 0), 0),
    [stats.playbooks],
  );
  const totalExemplars = useMemo(
    () => stats.playbooks.reduce((acc, p) => acc + (p.exemplar_count || 0), 0),
    [stats.playbooks],
  );

  return (
    <div className="px-6 py-6">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-brand-teal" />
          <h1 className="text-xl font-semibold text-slate-800">
            Creative Memory
          </h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-3xl">
          The reasoning ledger every Creative Memory–aware agent writes to.
          Persona routing verdicts, MCP tool calls, rule validations, and
          escalations all land here under{' '}
          <span className="font-mono text-slate-700">creative_memory:{'{'}playbook_id{'}'}</span>
          {' '}episodes. Routing decisions are scoped to the selected client
          {selectedClient && (
            <>
              {' '}
              (<span className="font-mono text-slate-700">{selectedClient.client_uid}</span>
              {orgUid && (
                <> · org <span className="font-mono text-slate-700">{orgUid}</span></>
              )}
              ).
            </>
          )}
        </p>
      </header>

      {stats.error && (
        <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Stats partially unavailable: {stats.error}
        </div>
      )}

      {/* Stat strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          icon={<BookOpen className="w-3.5 h-3.5 text-amber-500" />}
          label="Playbooks"
          value={stats.playbooks.length}
          subtitle={`${totalRules} rules · ${totalExemplars} exemplars`}
          to="/creative-memory/playbooks"
        />
        <StatCard
          icon={<RouteIcon className="w-3.5 h-3.5 text-violet-500" />}
          label="Routing decisions (7d)"
          value={stats.routingDecisionsLast7d}
          subtitle="PersonaConceptGraphRanker verdicts"
          to="/creative-memory/routing"
        />
        <StatCard
          icon={<History className="w-3.5 h-3.5 text-sky-500" />}
          label="All decisions"
          value={stats.recentDecisions.length}
          subtitle="filtered cross-playbook browser"
          to="/creative-memory/decisions"
        />
        <StatCard
          icon={<AlertTriangle className="w-3.5 h-3.5 text-rose-500" />}
          label="Open escalations"
          value={stats.escalationsOpen}
          subtitle="awaiting admin review"
          to="/creative-memory/escalations"
        />
      </div>

      {/* Most recent routing decisions */}
      <section className="mt-8">
        <header className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            Most recent persona-routing decisions
          </h2>
          <Link
            to="/creative-memory/routing"
            className="text-xs text-brand-teal hover:underline"
          >
            See all →
          </Link>
        </header>
        {stats.loading && stats.recentDecisions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-xs text-slate-500">
            Loading routing decisions…
          </div>
        ) : stats.recentDecisions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-xs text-slate-500">
            No routing decisions in the last 7 days for{' '}
            {selectedClient?.client_uid || 'this client'}. Run a creative
            review backfill with{' '}
            <span className="font-mono">PERSONA_RANKER_MODE=llm</span> and{' '}
            <span className="font-mono">CREATIVE_MEMORY_ENABLED=true</span> to
            populate this view.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {stats.recentDecisions.slice(0, 5).map((d) => (
              <li
                key={d.episode_id}
                className="rounded border border-slate-200 bg-white px-3 py-2"
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                  <span className="font-mono text-slate-700">
                    {d.creative_id}
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className="font-mono text-emerald-700">
                    {(d.selected_persona_ids || []).join(', ') || 'none'}
                  </span>
                  <span className="ml-auto text-[10px] text-slate-400">
                    {d.ranker_model && (
                      <span className="mr-2 inline-flex items-center gap-1">
                        <Cpu className="w-3 h-3" />
                        {d.ranker_model}
                      </span>
                    )}
                    {new Date(d.ts).toLocaleString()}
                  </span>
                </div>
                {d.overall_reasoning && (
                  <p className="mt-0.5 text-[11px] text-slate-600 truncate">
                    {d.overall_reasoning}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  subtitle: string;
  to: string;
}

function StatCard({ icon, label, value, subtitle, to }: StatCardProps) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-slate-200 bg-white px-3 py-3 hover:border-brand-teal/50 hover:shadow-sm transition"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-800">
        {value}
      </div>
      <div className="mt-0.5 text-[10px] text-slate-500 truncate">
        {subtitle}
      </div>
    </Link>
  );
}
